/**
 * Mix-ins guide — information only.
 * Reuses existing ingredient records and local images. Does not add herbs to cart.
 */
import { getIngredientBySlug, INGREDIENT_IMAGE_FALLBACK } from "./ingredients";

/** @typedef {{ slug: string, name: string, blurb: string, learnFirst?: boolean }} MixInCard */

/** @type {MixInCard[]} */
export const MIX_IN_CARDS = [
  {
    slug: "ginger",
    name: "Ginger",
    blurb: "Warm, sharp and popular in smoothies, juices and morning routines.",
  },
  {
    slug: "turmeric",
    name: "Turmeric",
    blurb: "Earthy and golden. Often paired with ginger or cinnamon.",
  },
  {
    slug: "cinnamon",
    name: "Cinnamon",
    blurb: "Sweet, warming and easy to mix into oats, smoothies or warm drinks.",
  },
  {
    slug: "elderberry",
    name: "Elderberry",
    blurb: "Deep berry flavour. Often used in seasonal-style blends.",
  },
  {
    slug: "hibiscus",
    name: "Hibiscus",
    blurb: "Tart, floral and colourful. Works well in fruity routines.",
  },
  {
    slug: "moringa",
    name: "Moringa",
    blurb: "Green and earthy. Often mixed into smoothies.",
  },
  {
    slug: "bladderwrack",
    name: "Bladderwrack",
    blurb: "A sea botanical often paired with sea moss.",
  },
  {
    slug: "burdock-root",
    name: "Burdock Root",
    blurb: "Earthy root powder, often used in traditional-style blends.",
  },
  {
    slug: "nettle",
    name: "Nettle",
    blurb: "Green, herbal and simple to pair with sea moss.",
  },
  {
    slug: "dandelion-root",
    name: "Dandelion Root",
    blurb: "Earthy and roasted-tasting. Often used in traditional routines.",
  },
  {
    slug: "matcha-green-tea",
    name: "Matcha / Green Tea",
    blurb: "Green, smooth and popular in drink-style routines.",
  },
  {
    slug: "chia",
    name: "Chia Seeds",
    blurb: "Small seeds often stirred into smoothies, oats or puddings.",
  },
  {
    slug: "hemp-seed",
    name: "Hemp Seed",
    blurb: "Mild, nutty seeds often added to smoothies or bowls.",
  },
  {
    slug: "milk-thistle",
    name: "Milk Thistle",
    blurb: "Traditional herb option. Learn before mixing.",
    learnFirst: true,
  },
  {
    slug: "flaxseed",
    name: "Flaxseed",
    blurb: "Mild, seed-based add-in for smoothies and oats.",
  },
  {
    slug: "green-tea-extract",
    name: "Green Tea Extract",
    blurb: "Concentrated green tea-style ingredient. Learn before mixing.",
    learnFirst: true,
  },
  {
    slug: "spirulina",
    name: "Spirulina",
    blurb: "Deep green powder with a strong taste, usually blended into smoothies.",
  },
  {
    slug: "beetroot-powder",
    name: "Beetroot Powder",
    blurb: "Bright red powder with an earthy taste, often used in drink-style mixes.",
  },
  {
    slug: "maca",
    name: "Maca",
    blurb: "Nutty, malty powder often used in smoothie routines.",
  },
  {
    slug: "ashwagandha",
    name: "Ashwagandha",
    blurb: "Traditional herb option. Learn before mixing.",
    learnFirst: true,
  },
];

export const MIX_IN_PAIRINGS = [
  {
    title: "Ginger + Turmeric",
    copy: "Warm, golden and easy to blend.",
  },
  {
    title: "Cinnamon + Maca",
    copy: "Smooth, warming and good for oats or shakes.",
  },
  {
    title: "Hibiscus + Elderberry",
    copy: "Fruity, deep-colour pairing.",
  },
  {
    title: "Chia + Hemp",
    copy: "Simple seed-based smoothie or bowl add-ins.",
  },
];

export function getMixInCards() {
  return MIX_IN_CARDS.map((card) => {
    const ing = getIngredientBySlug(card.slug);
    return {
      ...card,
      image: ing?.image || INGREDIENT_IMAGE_FALLBACK,
      imageAlt: ing?.imageAlt || `${card.name} mix-in`,
    };
  });
}
